import { Typography } from '@mui/material';
import { FunctionComponent, useState } from 'react';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';

import {
    StyledDialogContent,
    StyledDialogContainer,
    StyledHeaderContainer,
    StyledDialog,
    StyledHeaderTypography,
    StyledEmailTypography,
    StyledActionBox,
    StyledResendButton
} from './styled';

import {
    IconButtonStyled,
    PopupTextStyle,
    errorMessageStyle,
    successMessageStyle
} from '../../pages/styled';

import { resendActivationEmail } from '../../redux/user/service';
import useAppDispatch from '../../hooks/useAppDispatch';
import {
    getActivationErrorFromState,
    getActivationIsLoadingFromState,
    getActivationMessageFromState
} from '../../redux/user/selectors';

interface ActionDialogProps {
    open: boolean;
    onClose: () => void;
    email: string;
    accessToken?: string;
    mode?: 'registration' | 'invitation';
}

const ActionDialog: FunctionComponent<ActionDialogProps> = ({
    open,
    onClose,
    email,
    accessToken,
    mode = 'registration'
}) => {
    const [buttonText, setButtonText] = useState('Resend');

    const dispatch = useAppDispatch();

    const loading = useSelector(getActivationIsLoadingFromState);
    const updateError = useSelector(getActivationErrorFromState);
    const updateMessage = useSelector(getActivationMessageFromState);

    const handleResend = async () => {
        if (accessToken) {
            await dispatch(resendActivationEmail({ accessToken })).unwrap();
        }
    };

    const isRegistration = mode === 'registration';

    return (
        <StyledDialog open={open} onClose={onClose}>
            <StyledDialogContent>
                <StyledDialogContainer>
                    <StyledHeaderContainer>
                        <StyledHeaderTypography
                            variant="subtitle2"
                            component="div"
                        >
                            {isRegistration
                                ? 'Thank you for registering!'
                                : 'Invitation sent!'}
                        </StyledHeaderTypography>
                        <IconButtonStyled onClick={onClose} aria-label="close">
                            <CloseIcon fontSize="small" />
                        </IconButtonStyled>
                    </StyledHeaderContainer>

                    {updateMessage && (
                        <Typography variant="body2" sx={successMessageStyle}>
                            {updateMessage}
                        </Typography>
                    )}

                    {updateError && (
                        <Typography variant="body2" sx={errorMessageStyle}>
                            {updateError}
                        </Typography>
                    )}

                    <StyledEmailTypography variant="body2">
                        {isRegistration
                            ? `We've sent a confirmation email to ${email}.`
                            : `We've sent an invitation email to ${email}.`}
                    </StyledEmailTypography>

                    <Typography variant="body2" sx={PopupTextStyle}>
                        {isRegistration
                            ? "Please check your inbox and click the activation link to verify your account. If you don't see it, try checking your spam or junk folder."
                            : "The user will receive an email with instructions to join your team. If they don't see it, ask them to check their spam or junk folder."}
                    </Typography>

                    {isRegistration && (
                        <>
                            <Typography variant="body2" sx={PopupTextStyle}>
                                Still not there? Click below to resend the
                                email.
                            </Typography>

                            <StyledActionBox>
                                <StyledResendButton
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    onClick={handleResend}
                                    onMouseEnter={() => {
                                        setButtonText('Resend🐝');
                                    }}
                                    onMouseLeave={() => {
                                        setButtonText('Resend');
                                    }}
                                >
                                    {loading ? 'Sending...' : buttonText}
                                </StyledResendButton>
                            </StyledActionBox>
                        </>
                    )}
                </StyledDialogContainer>
            </StyledDialogContent>
        </StyledDialog>
    );
};

export default ActionDialog;
